/**
 * One thread, in the margin or in the panel — the same card either way.
 *
 * §8.1 describes an orphaned comment as one that "drops out of the margin, is
 * labeled as referring to removed content, and remains in an 'all comments'
 * panel". Two of those three are properties of *where* the card is listed, which
 * `comment-margin.tsx` decides from `inMargin`. The third is this file's whole
 * obligation: an orphaned card has to say, in words, that the text it quotes is
 * gone — and it has to keep the quote, because the record of an objection is
 * worth nothing without the words it was raised against.
 *
 * The reason §8.1 gives is worth repeating where the code is: somebody objects
 * to an indemnity clause, the clause is rewritten, and if the comment went with
 * it the record would be destroyed at the moment it mattered most. Nothing in
 * this card may hide a thread; the panel shows resolved and orphaned threads
 * with the same weight as live ones, differing only in their label.
 */

import type { ReactElement, ReactNode } from 'react';
import { useState } from 'react';
import { formatDateTime, formatRelative } from '../../components/format.ts';
import { Badge } from '../../components/ui/badge.tsx';
import { Button } from '../../components/ui/button.tsx';
import { cn } from '../../components/ui/cn.ts';
import { ErrorState } from '../../components/ui/feedback.tsx';
import { ReplyComposer } from './composer.tsx';
import { useApplySuggestion, useResolveThread, useWithdrawComment } from './queries.ts';
import { blockChangedLabel, orphanLabel, quoted } from './text.ts';
import type { ApplySuggestionResponse, CommentThreadView, CommentView } from './types.ts';

function Byline({ name, at }: { name: string; at: string }): ReactElement {
  return (
    <span className="flex flex-wrap items-baseline gap-x-2">
      <span className="text-sm font-medium text-primary">{name}</span>
      <span className="text-xs text-tertiary" title={formatDateTime(at)}>
        {formatRelative(at)}
      </span>
    </span>
  );
}

function Body({ children }: { children: ReactNode }): ReactElement {
  return <p className="text-sm whitespace-pre-line text-secondary">{children}</p>;
}

/**
 * §8.2, rendered as what it is: two versions of one passage.
 *
 * The old text is struck through and the new text sits under it, because a
 * suggestion is only reviewable if the reader can see both without holding one
 * in their head. An empty replacement is a proposal to delete, which is a real
 * suggestion and reads as nothing at all unless it is labelled.
 */
function Suggestion({
  anchorText,
  suggestedText,
}: {
  anchorText: string;
  suggestedText: string;
}): ReactElement {
  return (
    <div className="flex flex-col gap-1 rounded-md border border-secondary bg-secondary p-2.5 text-sm">
      <span className="text-xs font-semibold text-tertiary uppercase">Suggested change</span>
      <span className="text-error-primary line-through">{quoted(anchorText)}</span>
      <span className="text-success-primary">
        {suggestedText === '' ? (
          <span className="text-tertiary italic">delete this text</span>
        ) : (
          quoted(suggestedText)
        )}
      </span>
    </div>
  );
}

export interface CommentThreadCardProps {
  thread: CommentThreadView;
  nodeId: string;
  viewerId: string;
  /** §8.2's Apply, and §8.1's rule that closing someone else's thread is editorial. */
  canEdit: boolean;
  isSelected: boolean;
  onSelect: () => void;
  /** Scrolls the text into view; absent for a thread with nothing to point at. */
  onShowInText?: () => void;
  nameOf: (personId: string) => string;
  /** Handed the API's report so the live document can be brought into line (§8.3). */
  onApplied: (result: ApplySuggestionResponse) => void;
}

export function CommentThreadCard({
  thread,
  nodeId,
  viewerId,
  canEdit,
  isSelected,
  onSelect,
  onShowInText,
  nameOf,
  onApplied,
}: CommentThreadCardProps): ReactElement {
  const comment = thread.comment;
  const resolve = useResolveThread(nodeId);
  const withdraw = useWithdrawComment(nodeId);
  const apply = useApplySuggestion(nodeId);
  const [replying, setReplying] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const isAuthor = comment.authorId === viewerId;
  const orphaned = comment.state === 'orphaned';
  const resolved = comment.state === 'resolved';
  const isSuggestion = comment.suggestedText !== null;
  // Never on an orphaned suggestion: the server re-resolves the anchor and
  // refuses, and offering a button whose only outcome is that refusal invites
  // someone to press it and then wonder which words moved.
  const canApply = canEdit && isSuggestion && comment.appliedAt === null && !orphaned && !resolved;

  return (
    <article
      aria-label={`Comment by ${nameOf(comment.authorId)}`}
      className={cn(
        'flex flex-col gap-2.5 rounded-lg border p-3.5 transition-colors',
        isSelected ? 'border-brand bg-brand-primary' : 'border-secondary bg-primary',
        resolved && 'opacity-80',
      )}
      onFocus={onSelect}
    >
      <header className="flex flex-wrap items-center justify-between gap-2">
        <Byline name={isAuthor ? 'You' : nameOf(comment.authorId)} at={comment.createdAt} />
        <span className="flex flex-wrap items-center gap-1.5">
          {/* The audience, on every card. §8.1 makes this the fact about a
              comment that is most expensive to be wrong about, and a reader
              scanning a thread should never have to remember which one it is. */}
          <Badge tone={comment.internal ? 'brand' : 'success'}>
            {comment.internal ? 'Internal' : 'Client-visible'}
          </Badge>
          {isSuggestion ? <Badge tone="outline">Suggestion</Badge> : null}
          {orphaned ? <Badge tone="warning">Removed content</Badge> : null}
          {resolved ? <Badge tone="gray">Resolved</Badge> : null}
        </span>
      </header>

      {comment.anchor ? (
        <div className="flex flex-col gap-1">
          <blockquote
            className={cn(
              'border-l-2 pl-2 text-xs italic',
              orphaned ? 'border-warning text-tertiary line-through' : 'border-brand text-tertiary',
            )}
          >
            {comment.anchor.text === ''
              ? 'anchored to a block rather than to particular words'
              : `“${quoted(comment.anchor.text)}”`}
          </blockquote>
          {orphaned ? (
            <p className="text-xs text-warning-primary">{orphanLabel(comment.orphanReason)}</p>
          ) : comment.anchor.status === 'block_changed' ? (
            // Not an orphan and not styled like one: nothing was removed and
            // this is still anchored. §8.1's requirement is that the reader is
            // never left believing a comment is about the words currently
            // under it when it is not.
            <p className="text-xs text-warning-primary">{blockChangedLabel()}</p>
          ) : null}
        </div>
      ) : null}

      <Body>{comment.body}</Body>

      {isSuggestion && comment.anchor ? (
        <Suggestion
          anchorText={comment.anchor.text}
          suggestedText={comment.suggestedText as string}
        />
      ) : null}

      {comment.appliedAt ? (
        <p className="text-xs text-success-primary">
          Applied by {comment.appliedBy === viewerId ? 'you' : nameOf(comment.appliedBy ?? '')}{' '}
          {formatRelative(comment.appliedAt)}.
        </p>
      ) : null}

      {resolved && comment.resolvedBy ? (
        <p className="text-xs text-tertiary">
          Resolved by {comment.resolvedBy === viewerId ? 'you' : nameOf(comment.resolvedBy)}{' '}
          {formatRelative(comment.resolvedAt)}.
        </p>
      ) : null}

      {thread.replies.length > 0 ? (
        <ol className="flex flex-col gap-2 border-l border-secondary pl-3">
          {thread.replies.map((reply) => (
            <li key={reply.id} className="flex flex-col gap-1">
              <Byline
                name={reply.authorId === viewerId ? 'You' : nameOf(reply.authorId)}
                at={reply.createdAt}
              />
              <Body>{reply.body}</Body>
            </li>
          ))}
        </ol>
      ) : null}

      {replying ? (
        <ReplyComposer
          nodeId={nodeId}
          parentCommentId={comment.id}
          internal={comment.internal}
          onDone={() => setReplying(false)}
        />
      ) : null}

      {resolve.isError ? <ErrorState error={resolve.error} /> : null}
      {withdraw.isError ? <ErrorState error={withdraw.error} /> : null}
      {apply.isError ? <ErrorState error={apply.error} /> : null}

      <footer className="flex flex-wrap items-center gap-1">
        {onShowInText ? (
          <Button
            size="sm"
            variant="tertiary"
            className="px-1.5"
            onPress={() => {
              onSelect();
              onShowInText();
            }}
          >
            Show in text
          </Button>
        ) : null}
        {!replying && !resolved ? (
          <Button size="sm" variant="tertiary" className="px-1.5" onPress={() => setReplying(true)}>
            Reply
          </Button>
        ) : null}
        {canApply ? (
          <Button
            size="sm"
            variant="primary"
            isPending={apply.isPending}
            onPress={() => apply.mutate(comment.id, { onSuccess: onApplied })}
          >
            Apply suggestion
          </Button>
        ) : null}
        {!resolved && (isAuthor || canEdit) ? (
          <Button
            size="sm"
            variant="tertiary"
            className="px-1.5"
            isPending={resolve.isPending}
            onPress={() => resolve.mutate(comment.id)}
          >
            Resolve
          </Button>
        ) : null}
        {/* Withdrawal is soft in the database and final on screen: §8.1 retains
            the row, but `listComments` excludes a withdrawn comment and there is
            no route that brings one back. So the second click is the one that
            does it — a misfire beside Reply should not be how an objection
            leaves the record. */}
        {isAuthor && !confirming ? (
          <Button
            size="sm"
            variant="tertiary"
            className="px-1.5 text-error-primary"
            onPress={() => setConfirming(true)}
          >
            Withdraw
          </Button>
        ) : null}
        {isAuthor && confirming ? (
          <span className="flex flex-wrap items-center gap-1">
            <span className="text-xs text-tertiary">Withdraw this comment?</span>
            <Button
              size="sm"
              variant="destructive-secondary"
              isPending={withdraw.isPending}
              onPress={() => withdraw.mutate(comment.id)}
            >
              Withdraw
            </Button>
            <Button
              size="sm"
              variant="tertiary"
              className="px-1.5"
              onPress={() => setConfirming(false)}
            >
              Keep
            </Button>
          </span>
        ) : null}
      </footer>
    </article>
  );
}

/** Exported for the panel's counts, so "orphaned" is spelled once. */
export function isOrphaned(comment: CommentView): boolean {
  return comment.state === 'orphaned';
}
